import React from 'react';

const AboutMe = () => {
  return (
    <div>
      {/* Hero Section - Clean Background */}
      <section className="relative overflow-hidden min-h-screen -mt-20 bg-white">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-32 min-h-screen flex items-center">
          <div className="text-center max-w-5xl mx-auto w-full">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light mb-4 sm:mb-6 leading-[1.1] text-black tracking-tight">
              <span className="block">About</span>
              <span className="block">Dr. Todd Monos</span>
            </h1>
            
            {/* Tagline */}
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-black mb-6 sm:mb-8 font-light tracking-wide px-2">
              <span className="block sm:inline">Educator</span>
              <span className="hidden sm:inline mx-2">|</span>
              <span className="block sm:inline">Leader</span>
              <span className="hidden sm:inline mx-2">|</span>
              <span className="block sm:inline">Mentor</span>
              <span className="hidden sm:inline mx-2">|</span>
              <span className="block sm:inline">Community Advocate</span>
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section - Matching Home Mission Style */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-sm">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-black mb-4 sm:mb-6 px-2">
                Welcome
              </h2>
            </div>
            <div className="prose prose-lg sm:prose-xl max-w-none">
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed text-center px-2">
                Hi, I'm Dr. Todd Monos, a dedicated educator and school leader with over two decades of experience 
                in K–12 education and higher education instruction. I currently serve as a High School Assistant Principal 
                and teach as an adjunct professor at Millersville University and York College.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section - Matching Home Pillars Style */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight px-2">
              My Core Philosophy
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              Three fundamental principles guide everything I do as an educator, leader, and community advocate.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 mb-12 sm:mb-16">
            {/* Be Genuine */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Be Genuine</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Authentic relationships and honest communication form the foundation of meaningful educational experiences.
                </p>
              </div>
            </div>

            {/* Be Hard Working */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Be Hard Working</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Excellence requires dedication, persistence, and the commitment to continuous improvement in all endeavors.
                </p>
              </div>
            </div>

            {/* Help Others */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Help Others</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  True leadership means empowering others to reach their potential and creating positive change in our communities.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto px-4">
              Whether I'm leading instructional teams, designing learner-centered programs, or mentoring future 
              educators, I strive to create inclusive, innovative environments where students and staff can thrive.
            </p>
          </div>
        </div>
      </section>

      {/* Professional Journey - Similar to Home Four Pillars Style */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight px-2">
              Professional Journey
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              Over two decades of educational leadership, innovation, and community engagement across multiple institutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {/* Educational Leadership */}
            <div className="group">
              <div className="bg-white rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Educational Leadership</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  My journey has taken me through various leadership roles, from elementary principal to high school 
                  administrator, where I've championed equity, fostered community engagement, and streamlined systems 
                  for greater impact.
                </p>
              </div>
            </div>

            {/* Sonom Performance Institute */}
            <div className="group">
              <div className="bg-white rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Sonom Performance Institute</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  I also run Sonom Performance Institute, an innovative approach to athletic preparatory programming 
                  in the Central PA area, combining my passion for education with athletic development.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Roles - Similar to Home Program Stats */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight px-2">
              Current Roles
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
              Balancing educational leadership, higher education instruction, and athletic performance development 
              across multiple institutions and organizations.
            </p>
          </div>

          {/* Role Cards */}
          <div className="bg-gradient-to-r from-spi-600 to-spi-700 rounded-2xl p-6 sm:p-8 lg:p-12 text-white text-center mb-8 sm:mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
              <div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">High School Assistant Principal</h3>
                <p className="text-spi-200 text-sm sm:text-base">Leading instructional teams and fostering student success</p>
              </div>
              
              <div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Adjunct Professor</h3>
                <p className="text-spi-200 text-sm sm:text-base">Millersville University & York College</p>
              </div>
              
              <div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">SPI Director</h3>
                <p className="text-spi-200 text-sm sm:text-base">Sonom Performance Institute</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Service - Matching Home Mission Style */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-sm">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-black mb-4 sm:mb-6 px-2">
                Community Commitment
              </h2>
            </div>
            <div className="prose prose-lg sm:prose-xl max-w-none">
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed text-center px-2">
                When I'm not working, I volunteer with <strong>Compass</strong> and <strong>Water Street Mission</strong>, 
                continuing my commitment to service and community. These organizations allow me to extend my passion 
                for helping others beyond the educational sphere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact - Matching Home Contact Style */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight px-2">
              Let's Connect
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
              Let's connect and explore how we can make a meaningful difference together.
            </p>
          </div>

          {/* Contact Information */}
          <div className="text-center px-4">
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Ready to explore collaboration opportunities? Reach out to discuss education, leadership, or community service.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
              <a 
                href="mailto:todd@sonomperformance.com"
                className="bg-black hover:bg-gray-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base text-center"
              >
                <span className="block sm:inline">Get in Touch</span>
              </a>
              <a 
                href="/support-spi"
                className="border border-black text-black hover:bg-black hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base text-center"
              >
                Learn About SPI
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutMe;
